import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "@store/baseQuery";
import type {
    ICreateLiveboardPayload,
    ICreateLiveboardResponse,
    IExportToTSResponse,
    IGetLiveboardsPayload,
    IPostTSAConfigPayload,
    IPostTSEResponse,
    TGetLiveboardsResponse,
    IGetDatabricksDashboardsResponse,
} from "@utils/interface";

type GetBrandKitPayload = {
    url: string;
    processingId: number;
};

type UploadDemoLogoPayload = {
    file: FormData;
    processingId: number;
};

type DeleteDemoLogoPayload = {
    url: string;
    processingId: number;
};

export const dashboardApi = createApi({
    reducerPath: "dashboardApi",
    baseQuery: customBaseQuery,
    tagTypes: ["DemoLabState", "Entities"],
    endpoints: (builder) => ({
        generateDataset: builder.mutation({
            query: (data) => ({
                url: "demo_chat/dataset_generation",
                method: "POST",
                body: data,
            }),
        }),

        generateSampleData: builder.query({
            query: (filename) => ({
                url: `demo_flow/sample_data/${filename}`,
                method: "GET",
            }),
        }),

        demoFlowSave: builder.mutation({
            query: (data) => ({
                url: `demo_flow/ts_data/`,
                method: "POST",
                body: data,
            }),
        }),

        demoFlowGet: builder.query({
            query: (id) => ({
                url: `demo_flow/ts_data/${id}/`,
                method: "GET",
            }),
        }),

        uploadToSnowflake: builder.mutation({
            query: (id) => ({
                url: `processing/results/export`,
                method: "POST",
                body: {
                    model_id: id,
                },
            }),
        }),

        exportToThoughtspot: builder.mutation<IExportToTSResponse, number>({
            query: (id) => ({
                url: `demo_flow/export_to_ts`,
                method: "POST",
                body: {
                    model_id: id,
                },
            }),
        }),

        createThoughtspotLiveboard: builder.mutation<ICreateLiveboardResponse, ICreateLiveboardPayload>({
            query: (body) => ({
                url: `demo_flow/create_liveboard`,
                method: "POST",
                body,
            }),
        }),

        postTsFormulas: builder.mutation({
            query: (body) => ({
                url: `demo_flow/ts_formulas/`,
                method: "POST",
                body,
            }),
        }),

        getTsToken: builder.query({
            query: () => ({
                url: `demo_flow/ts_token`,
                method: "GET",
            }),
        }),

        getLiveboards: builder.mutation<TGetLiveboardsResponse, IGetLiveboardsPayload>({
            query: (body) => ({
                url: `demo_flow/metadata-search`,
                method: "POST",
                body: body,
            }),
        }),

        postTSEConfigs: builder.mutation<IPostTSEResponse, IPostTSAConfigPayload>({
            query: (body) => ({
                url: `demo_flow/tse-page-config/`,
                method: "POST",
                body: body,
            }),
        }),

        getTSEConfigs: builder.query({
            query: (params) => ({
                url: `/demo_flow/tse-page-config/${params}`,
                method: "GET",
            }),
        }),

        getBrandKit: builder.mutation<any, GetBrandKitPayload>({
            query: ({ url, processingId }) => ({
                url: `demo_flow/brand_dev_data`,
                method: "POST",
                body: {
                    domain: url,
                    processing_id: processingId,
                },
            }),
        }),

        uploadFile: builder.mutation({
            query: ({ file, filename }: { file: FormData; filename: string }) => ({
                url: `demo_flow/upload?filename=${encodeURIComponent(filename)}`,
                method: "POST",
                body: file,
            }),
        }),

        uploadDocument: builder.mutation<
            any,
            { entityId: number; file: File }
        >({
            query: ({ entityId, file }) => {
                const formData = new FormData();
                formData.append("file", file);

                return {
                    url: `/demo-lab/${entityId}/files/upload/`,
                    method: "POST",
                    body: formData,
                };
            },
        }),

        uploadStepRequiredFile: builder.mutation<
            any,
            { entityId: number; step: number; file: File }
        >({
            query: ({ entityId, step, file }) => {
                const formData = new FormData();
                formData.append("file", file);

                return {
                    url: `/demo-lab/${entityId}/steps/${step}/files/upload/`,
                    method: "POST",
                    body: formData,
                };
            },
            invalidatesTags: ["DemoLabState"],
        }),

        uploadCribSheetPdf: builder.mutation<
            any,
            { entityId: number; file: Blob }
        >({
            query: ({ entityId, file }) => {
                const formData = new FormData();
                formData.append("file", file, "CribSheet.pdf");

                return {
                    url: `/demo-lab/${entityId}/steps/9/crib-sheet-pdf/`,
                    method: "POST",
                    body: formData,
                };
            },
        }),

        demoMetadataCreate: builder.mutation({
            query: (body) => ({
                url: `demo_flow/demo-metadata`,
                method: "POST",
                body,
            }),
        }),

        generateVersal: builder.mutation({
            query: (body) => ({
                url: `config_deployment/save_config`,
                method: "POST",
                body,
            }),
        }),

        updateSemanticAssets: builder.mutation({
            query: (id) => ({
                url: `demo_flow/snowflake_semantic_assets`,
                method: "POST",
                body: {
                    model_id: id,
                },
            }),
        }),

        uploadDemoLogo: builder.mutation<{ url: string }, UploadDemoLogoPayload>({
            query: ({ file, processingId }) => {
                file.append("processing_id", String(processingId));

                return {
                    url: `demo_flow/logo`,
                    method: "POST",
                    body: file,
                };
            },
        }),

        deleteDemoLogo: builder.mutation<any, DeleteDemoLogoPayload>({
            query: ({ url, processingId }) => ({
                url: `demo_flow/logo`,
                method: "DELETE",
                body: {
                    url,
                    processing_id: processingId,
                },
            }),
        }),

        generateDatabricksBIAssets: builder.mutation({
            query: (body) => ({
                url: `demo_flow/databricks/generate_bi_assets`,
                method: "POST",
                body,
            }),
        }),

        /* ------------------ Polling ------------------ */
        getDemoLabState: builder.query({
            query: (id) => ({
                url: `demo-lab/${id}/state/`,
                method: "GET",
            }),
            providesTags: (_res, _err, id) => [{ type: "DemoLabState", id }],
            keepUnusedDataFor: 0,
        }),

        submitStep: builder.mutation({
            query: ({ step, id, data }) => ({
                url:
                    String(step) === "1"
                        ? `demo-lab/steps/1/`
                        : `demo-lab/${id}/steps/${step}/`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_res, _err, { id }) =>
                id ? [{ type: "DemoLabState", id }] : [],
        }),

        regenerateStep: builder.mutation({
            query: ({ step, id }) => ({
                url: `demo-lab/${id}/steps/${step}/regenerate/`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, { id }) => [
                { type: "DemoLabState", id },
            ],
        }),

        getEntitiesList: builder.query({
            query: () => ({
                url: `demo-lab/entities/`,
                method: "GET",
            }),
            providesTags: [{ type: "Entities", id: "LIST" }],
        }),

        switchStatus: builder.mutation({
            query: ({ id, data }) => ({
                url: `demo-lab/${id}/state/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: [{ type: "Entities", id: "LIST" }],
        }),

        deleteEntity: builder.mutation({
            query: ({ id }) => ({
                url: `demo-lab/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "Entities", id: "LIST" }],
        }),

        patchDemoLabEntity: builder.mutation({
            query: ({ id, data }) => ({
                url: `demo-lab/${id}/state/`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: (_res, _err, { id }) => [{ type: "DemoLabState", id }],
        }),

        startDemoLabDataUpload: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/10/upload/`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        generateDatasourceAssets: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/generate-assets/`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        exportDemoLabToThoughtspot: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/11/export-to-thoughtspot/`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        createSnowflakeSemanticAssets: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/12/create-snowflake-semantic-assets/`,
                method: "POST",
                body: {},
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        exportDemoLabToDatabricks: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/12/create-genie-space/`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        createliveboard: builder.mutation<void, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/13/create-liveboard/`,
                method: "POST"
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        createDashboardDatabricks: builder.mutation<unknown, { entityId: number }>({
            query: ({ entityId }) => ({
                url: `demo-lab/${entityId}/steps/13/create-dashboard-databricks/`,
                method: "POST"
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        runSilentMode: builder.mutation<void, number>({
            query: (id) => ({
                url: `demo-lab/${id}/silent-mode/`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: "DemoLabState", id },
            ],
        }),

        stopSilentMode: builder.mutation<void, number>({
            query: (id) => ({
                url: `demo-lab/${id}/silent-mode/stop/`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: "DemoLabState", id },
            ],
        }),

        saveConfig: builder.mutation({
            query: ({ config, entityId }) => ({
                url: `demo-lab/${entityId}/save-config/`,
                method: "POST",
                body: config,
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        pushToTarget: builder.mutation({
            query: ({ entityId, ...body }) => ({
                url: `demo-lab/${entityId}/steps/10/`,
                method: "POST",
                body,
            }),
            invalidatesTags: (_res, _err, { entityId }) =>
                entityId ? [{ type: "DemoLabState", entityId }] : [],
        }),

        clusterStatus: builder.mutation({
            query: (body) => ({
                url: `/integrations/connections/cluster-status/`,
                method: "POST",
                body,
            }),
        }),

        getTemplateUploadUrl: builder.query<{ uploadUrl: string }, void>({
            query: () => ({
                url: `demo-lab/template-upload-url/`,
                method: "GET",
            }),
        }),
        getThoughtspotLiveboards: builder.query<any, number>({
            query: (pipeline_id) => ({
                url: `demo-lab/thoughtspot-liveboards/`,
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        getThoughtspotSpotterModels: builder.query<any, number>({
            query: (pipeline_id) => ({
                url: `demo-lab/thoughtspot-spotter-models/`,
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        getDatabricksDashboards: builder.query<IGetDatabricksDashboardsResponse, number>({
            query: (pipeline_id) => ({
                url: `demo-lab/databricks-dashboards/`,
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        getDatabricksGenieSpaces: builder.query<any, number>({
            query: (pipeline_id) => ({
                url: `demo-lab/databricks-genie-spaces/`,
                method: "POST",
                body: { pipeline_id },
            }),
        }),
    }),
});

export const {
    useGenerateDatasetMutation,
    useLazyGenerateSampleDataQuery,
    useDemoFlowSaveMutation,
    useLazyDemoFlowGetQuery,
    useUploadToSnowflakeMutation,
    useExportToThoughtspotMutation,
    useCreateThoughtspotLiveboardMutation,
    usePostTsFormulasMutation,
    useLazyGetTsTokenQuery,
    useGetLiveboardsMutation,
    usePostTSEConfigsMutation,
    useLazyGetTSEConfigsQuery,
    useGetBrandKitMutation,
    useUploadFileMutation,
    useDemoMetadataCreateMutation,
    useGenerateVersalMutation,
    useUpdateSemanticAssetsMutation,
    useUploadDemoLogoMutation,
    useDeleteDemoLogoMutation,
    useGenerateDatabricksBIAssetsMutation,
    useGetDemoLabStateQuery,
    useLazyGetDemoLabStateQuery,
    useSubmitStepMutation,
    useRegenerateStepMutation,
    useLazyGetEntitiesListQuery,
    useUploadDocumentMutation,
    useSwitchStatusMutation,
    useDeleteEntityMutation,
    usePatchDemoLabEntityMutation,
    useStartDemoLabDataUploadMutation,
    useGenerateDatasourceAssetsMutation,
    useExportDemoLabToThoughtspotMutation,
    useCreateSnowflakeSemanticAssetsMutation,
    useExportDemoLabToDatabricksMutation,
    useCreateliveboardMutation,
    useCreateDashboardDatabricksMutation,
    useSaveConfigMutation,
    useRunSilentModeMutation,
    useStopSilentModeMutation,
    useUploadStepRequiredFileMutation,
    useUploadCribSheetPdfMutation,
    usePushToTargetMutation,
    useClusterStatusMutation,
    useLazyGetTemplateUploadUrlQuery,
    useGetThoughtspotLiveboardsQuery,
    useGetThoughtspotSpotterModelsQuery,
    useGetDatabricksDashboardsQuery,
    useGetDatabricksGenieSpacesQuery,
} = dashboardApi;
